import React, { useMemo } from 'react';
import { mapValues } from 'lodash';
import cx from 'classnames';

import styles from './Churn.module.scss';

const strokeWidth = 1;

function Sprint({ sprint, barWidth, gapWidth, yScale, chartHeight, units }) {
  const { metrics } = sprint;
  const { initial, remaining, growth, shrinkage, added, removed } =
    useMemo(() => {
      const initial = mapValues(metrics.initial, (value) => value[units]);
      const remaining = mapValues(metrics.remaining, (value) => value[units]);
      const growth = metrics.growth[units];
      const added = metrics.added_and_not_removed[units];
      const shrinkage = metrics.shrinkage[units];
      const removed = metrics.removed_from_initial[units];
      // const growth = 3;
      // const added = 2;
      // const shrinkage = 3
      // const removed = 2;

      return {
        initial,
        remaining,
        growth,
        shrinkage,
        added,
        removed,
      };
    }, [metrics, units]);

  const { steps } = useMemo(() => {
    return [
      { step: growth, style: styles.growth },
      { step: added, style: styles.added },
      { step: -shrinkage, style: styles.shrinkage },
      { step: -removed, style: styles.removed },
    ].reduce(
      (acc, { step, style }) => {
        acc.steps.push({
          y: acc.current,
          height: step,
          style,
        });
        acc.current += step;
        return acc;
      },
      {
        steps: [],
        current: initial.done + initial.indeterminate + initial.todo,
      }
    );
  }, [initial, growth, shrinkage, added, removed]);

  const isCompleted = sprint.sprint.complete_date !== null;

  return (
    <g>
      <g>
        <g>
          <rect
            x={-gapWidth / 2}
            y={0}
            width={barWidth + gapWidth}
            height={chartHeight}
            className={cx(styles.start, styles['start-end-box'])}
          />
          <rect
            x={3 * barWidth + 4.5 * gapWidth}
            y={0}
            width={barWidth + gapWidth}
            height={chartHeight}
            className={cx(
              {
                [styles.end]: isCompleted,
                [styles.current]: !isCompleted,
              },
              styles['start-end-box']
            )}
          />
        </g>
      </g>
      <g>
        <line
          x1={barWidth}
          y1={yScale(initial.done + initial.indeterminate + initial.todo)}
          x2={barWidth + gapWidth}
          y2={yScale(initial.done + initial.indeterminate + initial.todo)}
          className={styles['step-line']}
        />
        {steps.map(({ y, height: stepHeight }, index) => {
          const x =
            barWidth +
            gapWidth +
            index * (barWidth / 2 + gapWidth) +
            barWidth / 2;

          return (
            <line
              key={index}
              x1={x}
              y1={yScale(y + stepHeight)}
              x2={x + gapWidth}
              y2={yScale(y + stepHeight)}
              className={styles['step-line']}
            />
          );
        })}
      </g>
      <g>
        <rect
          x={strokeWidth / 2}
          y={yScale(initial.done + initial.indeterminate + initial.todo)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(initial.done)}
          className={cx(styles.box, styles.done)}
        />
        <rect
          x={strokeWidth / 2}
          y={yScale(initial.todo + initial.indeterminate)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(initial.todo)}
          className={cx(styles.box, styles.todo)}
        />
        <rect
          x={strokeWidth / 2}
          y={yScale(initial.indeterminate)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(initial.indeterminate)}
          className={cx(styles.box, styles.indeterminate)}
        />
      </g>
      <g
        transform={`translate(${barWidth + gapWidth}, 0)`}
        style={{ display: 'xnone' }}
      >
        {steps.map(({ y, height: stepHeight, style }, index) => {
          const yPosition = stepHeight > 0 ? yScale(y + stepHeight) : yScale(y);

          return (
            <g
              key={index}
              transform={`translate(${
                index * (barWidth / 2 + gapWidth) + strokeWidth / 2
              }, 0)`}
            >
              <rect
                x={0}
                y={yPosition}
                width={0.5 * barWidth - strokeWidth}
                height={Math.abs(yScale(y + stepHeight) - yScale(y)) || 0.01}
                className={cx(styles.box, style)}
              />
              {false && stepHeight !== 0 && (
                <text
                  x={barWidth / 4}
                  y={
                    stepHeight < 0
                      ? yPosition +
                        3 +
                        Math.abs(yScale(y + stepHeight) - yScale(y))
                      : yPosition - 3
                  }
                  dy={stepHeight < 0 ? '0.8em' : '0em'}
                  textAnchor="middle"
                  fontSize="0.6rem"
                  className={cx(styles['step-label'], {
                    [styles['step-label-positive']]: stepHeight > 0,
                    [styles['step-label-negative']]: stepHeight < 0,
                  })}
                >
                  {Math.round(Math.abs(stepHeight))}
                </text>
              )}
            </g>
          );
        })}
      </g>
      <g transform={`translate(${3 * barWidth + 5 * gapWidth}, 0)`}>
        <rect
          x={strokeWidth / 2}
          y={yScale(remaining.done + remaining.indeterminate + remaining.todo)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(remaining.done)}
          className={styles.done}
        />
        <rect
          x={strokeWidth / 2}
          y={yScale(remaining.todo + remaining.indeterminate)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(remaining.todo)}
          className={styles.todo}
        />
        <rect
          x={strokeWidth / 2}
          y={yScale(remaining.indeterminate)}
          width={barWidth - strokeWidth}
          height={chartHeight - yScale(remaining.indeterminate)}
          className={styles.indeterminate}
        />
      </g>
    </g>
  );
}

export default Sprint;
